/**
 * Attract-mode pilot (GDD §7.1). It flies through the same PilotInput as a player (GDD §11, D17). Its line and its
 * reactions are the concept's (aiDecide, concept L1146–L1183): it weaves down the river, seeks fuel below 70%,
 * lines up on threats and fires, dodges what it can't shoot in time, holds fire over a barge it needs, shoots the
 * bridge from its section's range, sidesteps incoming shots it notices, and slows for crossing jets.
 */
import { clamp, wrapDz } from '../core/math.ts';
import { config } from '../core/config.ts';
import { ISLAND_Z, type WorldProfile } from '../world/profiles.ts';
import type { GameSim } from './context.ts';
import type { FlightState } from './flight.ts';
import type { PilotInput } from './pilot.ts';
import type { Enemy } from './state.ts';

export interface AiDecision {
  /** Wanted x 34 m ahead. */
  target: number;
  fire: boolean;
  /** Slow down for a crossing enemy jet (D17: throttle −1). */
  slow: boolean;
}

/** The line the autopilot wants 34 m ahead: a lazy weave, one channel beside the island, never near a bank. */
export function autopilotTarget(s: Pick<FlightState, 't' | 'x' | 'z'>, W: WorldProfile): number {
  const za = s.z - 34;
  const c = W.center(za);
  const w = W.width(za);
  let target = c + w * 0.26 * Math.sin(s.t * 0.43) + w * 0.1 * Math.sin(s.t * 1.27);
  if (W.island && Math.abs(wrapDz(za - ISLAND_Z)) < 78) target = c + (s.x >= c ? 1 : -1) * 0.7 * w;
  const lim = Math.max(w - 11, 4);
  return clamp(target, c - lim, c + lim);
}

/** concept aiDecide (L1146–L1183) on the game simulation; `t` is the run clock. Writes into `out`. */
export function aiDecide(
  s: Pick<FlightState, 'x' | 'z' | 'speed'>,
  t: number,
  W: WorldProfile,
  sim: GameSim,
  out: AiDecision,
): AiDecision {
  const run = sim.run;
  const za = s.z - 34;
  const c = W.center(za);
  const w = W.width(za);
  let target = c + w * 0.26 * Math.sin(t * 0.43) + w * 0.1 * Math.sin(t * 1.27);
  let fire = false;
  const B = sim.bridge;
  const bAhead = s.z - B.z;
  const bridgeFire = B.intact && bAhead > 0 && bAhead < run.bridgeRange;
  const wantFuel = run.fuel < 0.7;
  let fuelE: Enemy | null = null;
  let threat: Enemy | null = null;
  let td = 1e9;
  const ents = sim.ents;
  for (let i = 0; i < ents.count; i++) {
    const e = ents.items[i];
    if (!e || e.dying > 0) continue;
    const d = s.z - e.z;
    if (d < -6 || d > 260) continue;
    if (e.type === 'fuel') {
      if (d > 8 && (!fuelE || d < s.z - fuelE.z)) fuelE = e;
      continue;
    }
    const tt = d / Math.max(s.speed, 1);
    const px = e.x + (e.move || e.type === 'jet' ? e.vx * tt : 0);
    if (e.type !== 'jet' && Math.abs(px - s.x) < 24 && d < td) {
      threat = e;
      td = d;
      e.px = px;
    }
  }
  const goFuel = fuelE !== null && wantFuel && s.z - fuelE.z < 320;
  if (goFuel && fuelE) target = fuelE.x;
  if (threat) {
    const aligned = Math.abs(threat.px - s.x) < threat.hw + 1.5;
    const dodgeX = threat.px + (s.x >= threat.px ? 1 : -1) * (threat.hw + 12);
    if (td < 48 || (td < 100 && !aligned)) target = dodgeX;
    else if (!goFuel || (fuelE && td < s.z - fuelE.z)) target = threat.px;
    if (aligned && td < 240) fire = true;
  }
  if (
    fire &&
    fuelE &&
    run.fuel < 0.92 &&
    Math.abs(fuelE.x - s.x) < fuelE.hw + 2 &&
    s.z - fuelE.z < 260 &&
    !(threat && td < s.z - fuelE.z)
  )
    fire = false;
  if (bridgeFire && (!fuelE || bAhead < s.z - fuelE.z)) fire = true;
  const shots = sim.shots;
  for (let i = 0; i < shots.count; i++) {
    const sh = shots.items[i];
    if (!sh || !sh.dodge) continue;
    const cl = sh.vz + s.speed;
    if (cl <= 0) continue;
    const tt2 = (s.z - sh.z) / cl;
    if (tt2 < 0 || tt2 > 1.0) continue;
    const xp = sh.x + sh.vx * tt2;
    if (Math.abs(xp - s.x) < 5.5) {
      target = s.x + (xp > s.x ? -13 : 13);
      break;
    }
  }
  let slow = false;
  for (let i = 0; i < ents.count; i++) {
    const ej = ents.items[i];
    if (!ej || ej.type !== 'jet' || ej.dying > 0) continue;
    const dj = s.z - ej.z;
    if (dj > 0 && dj < 75) {
      const tx = (s.x - ej.x) / ej.vx;
      if (tx > -0.2 && tx < 1.5) slow = true;
    }
  }
  if (W.island && Math.abs(wrapDz(za - ISLAND_Z)) < 78) target = c + (s.x >= c ? 1 : -1) * 0.7 * w;
  const lim = Math.max(w - 11, 4);
  out.target = clamp(target, c - lim, c + lim);
  out.fire = fire && run.alive && run.title <= 3;
  out.slow = slow;
  return out;
}

const decision: AiDecision = { target: 0, fire: false, slow: false };

/**
 * Writes the autopilot's input into `out` (no allocation). The concept eased its x toward the target at 2.4/s;
 * here that becomes a sideways-velocity request of (target − x)·2.4, as a steer axis. With a game running it also
 * fires, and slows to 26 m/s for crossing jets (D17: throttle −1, never below the player's range).
 */
export function autopilot(
  s: FlightState,
  W: WorldProfile,
  sensitivity: number,
  out: PilotInput,
  sim: GameSim | null = null,
): PilotInput {
  const f = config.flight;
  let target: number;
  if (sim) {
    aiDecide(s, s.t, W, sim, decision);
    target = decision.target;
    out.fire = decision.fire;
    out.throttle = decision.slow ? -1 : 0;
  } else {
    target = autopilotTarget(s, W);
    out.fire = false;
    out.throttle = 0;
  }
  out.steer = clamp(((target - s.x) * f.steerResponse) / (f.lateralSpeed * sensitivity), -1, 1);
  return out;
}
